import { LogLevel } from './types';

/**
 * 日志系统
 * 提供结构化的日志记录，可以根据环境配置日志级别
 */
class Logger {
  private static instance: Logger;
  private logLevel: LogLevel = LogLevel.INFO;
  private isProduction: boolean = import.meta.env.PROD;

  private constructor() {
    // 在生产环境中默认只显示警告和错误
    if (this.isProduction) {
      this.logLevel = LogLevel.WARN;
    }
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * 设置日志级别
   */
  public setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  /**
   * 记录调试信息
   */
  public debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(`🔍 [DEBUG] ${message}`, ...args);
    }
  }

  /**
   * 记录一般信息
   */
  public info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`ℹ️ [INFO] ${message}`, ...args);
    }
  }

  /**
   * 记录警告信息
   */
  public warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`⚠️ [WARN] ${message}`, ...args);
    }
  }

  /**
   * 记录错误信息
   */
  public error(message: string, error?: any, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`❌ [ERROR] ${message}`, error, ...args);
    }
  }

  /**
   * 判断是否应该记录指定级别的日志
   */
  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const targetLevelIndex = levels.indexOf(level);

    return targetLevelIndex >= currentLevelIndex;
  }
}

// 导出单例实例
export const logger = Logger.getInstance();

// 导出日志函数，方便直接使用
export const debug = (message: string, ...args: any[]) => logger.debug(message, ...args);
export const info = (message: string, ...args: any[]) => logger.info(message, ...args);
export const warn = (message: string, ...args: any[]) => logger.warn(message, ...args);
export const error = (message: string, error?: any, ...args: any[]) => logger.error(message, error, ...args);